// components/SearchDetailPopover.jsx — OnSumn Spotlight mobile detail popover.
//
// Ports the FLIP-morph + portal + scroll-lock pattern from MerchPopover.jsx.
// In Spotlight search this renders only on viewports <768px (desktop search
// uses the inline 2-column pane). It is ALSO reused on desktop /flows, where the
// station-detail cards (FlowsDetail.jsx) open it — there is no 2-column pane
// there, so the modal form is the detail surface. Viewport-agnostic by behavior.
//
// Required props:
//   item        — { kind: 'event'|'venue', event?, venue?, eventCount? }
//   darkMode    — boolean
//   triggerRect — DOMRect captured at click time (for FLIP origin); falsy → fade only
//   onClose     — () => void
//
// Window-attached at file bottom per OnSumn convention.

function SearchDetailPopover({ item, darkMode, triggerRect, onClose }) {
  const panelRef = React.useRef(null);
  const closeBtnRef = React.useRef(null);
  const previouslyFocusedRef = React.useRef(null);
  const [closing, setClosing] = React.useState(false);

  const reducedMotion = typeof window !== 'undefined'
    && typeof window.useReducedMotion === 'function'
    ? window.useReducedMotion()
    : (typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  // ─── FLIP morph (open) ───
  React.useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    if (reducedMotion || !triggerRect) {
      panel.style.transition = 'opacity 200ms ease';
      panel.style.opacity = '0';
      panel.getBoundingClientRect();
      requestAnimationFrame(() => { panel.style.opacity = '1'; });
      return;
    }

    const toRect = panel.getBoundingClientRect();
    const dx = triggerRect.left - toRect.left;
    const dy = triggerRect.top - toRect.top;
    const sx = Math.max(0.06, triggerRect.width / toRect.width);
    const sy = Math.max(0.06, triggerRect.height / toRect.height);

    panel.style.transformOrigin = 'top left';
    panel.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
    panel.style.opacity = '0.85';
    panel.getBoundingClientRect();

    requestAnimationFrame(() => {
      panel.style.transition =
        'transform 380ms cubic-bezier(0.16, 1, 0.3, 1), ' +
        'opacity 220ms ease 50ms';
      panel.style.transform = 'translate(0, 0) scale(1, 1)';
      panel.style.opacity = '1';
    });
  }, [triggerRect, reducedMotion]);

  // ─── ESC + scroll lock + focus management ───
  React.useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    // Capture-phase + stopPropagation: this popover is the topmost layer
    // (z 2000, aria-modal) wherever it mounts, so it must CONSUME Escape
    // before any lower layer's document/bubble listener sees it. Without
    // this, FlowsApp's desktop-detail Esc dismiss (document-level, fires
    // before a window bubble listener) closes the whole station panel and
    // unmounts this popover mid-open — no FLIP reverse, focus dumped on
    // the feed row (QA catch, goal 3).
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); handleClose(); } };
    window.addEventListener('keydown', onKey, true);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTimer = setTimeout(() => {
      if (closeBtnRef.current) closeBtnRef.current.focus();
    }, 180);

    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
      clearTimeout(focusTimer);
      if (previouslyFocusedRef.current && typeof previouslyFocusedRef.current.focus === 'function') {
        try { previouslyFocusedRef.current.focus(); } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── FLIP morph (close) — reverse ───
  const handleClose = React.useCallback(() => {
    if (closing) return;
    const panel = panelRef.current;
    if (!panel || reducedMotion || !triggerRect) {
      onClose();
      return;
    }
    setClosing(true);

    const fromRect = panel.getBoundingClientRect();
    const dx = triggerRect.left - fromRect.left;
    const dy = triggerRect.top - fromRect.top;
    const sx = Math.max(0.06, triggerRect.width / fromRect.width);
    const sy = Math.max(0.06, triggerRect.height / fromRect.height);

    panel.style.transition =
      'transform 300ms cubic-bezier(0.4, 0, 1, 1), opacity 200ms ease 60ms';
    panel.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
    panel.style.opacity = '0';

    setTimeout(() => onClose(), 300);
  }, [closing, onClose, reducedMotion, triggerRect]);

  if (!item) return null;

  const surface = darkMode ? '#121414' : '#F8F8F8';
  const onSurface = darkMode ? '#E2E2E2' : '#1A1A1A';
  const hairline = darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)';

  const panelGlass = typeof window !== 'undefined' && typeof window.glassStyle === 'function'
    ? window.glassStyle(darkMode, 'panel')
    : { background: surface };

  const btnGlass = typeof window !== 'undefined' && typeof window.glassStyle === 'function'
    ? window.glassStyle(darkMode, 'btn')
    : {};

  const stop = (e) => e.stopPropagation();

  const overlay = (
    <div
      role="presentation"
      onClick={(e) => { stop(e); handleClose(); }}
      style={{
        // zIndex bumped 1050 → 2000 to stack ABOVE the mobile search bottom
        // sheet (z=1100) + its scrim (z=1099). Without this the popover
        // would render hidden behind the sheet. Close popover → return to
        // sheet; close sheet → return to page.
        position: 'fixed', inset: 0, zIndex: 2000,
        background: darkMode ? 'rgba(8,8,10,0.55)' : 'rgba(40,30,20,0.32)',
        backdropFilter: 'blur(10px) saturate(120%)',
        WebkitBackdropFilter: 'blur(10px) saturate(120%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'clamp(12px, 4vw, 24px)',
        animation: reducedMotion ? 'none' : 'search-detail-backdrop-in 200ms ease both',
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="search-detail-title"
        onClick={stop}
        style={{
          ...panelGlass,
          border: `1px solid ${hairline}`,
          borderRadius: 20,
          width: 'min(440px, 100%)',
          maxHeight: '88vh', overflowY: 'auto',
          padding: 'clamp(18px, 4vw, 24px)',
          position: 'relative',
          willChange: 'transform, opacity',
          boxShadow: (panelGlass.boxShadow || '') +
            ', 0 24px 64px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04)',
        }}
      >
        {/* Close button — 44px tap target, top-right */}
        <button
          ref={closeBtnRef}
          type="button"
          onClick={handleClose}
          aria-label="Close preview"
          style={{
            ...btnGlass,
            position: 'absolute', top: 12, right: 12,
            width: 44, height: 44, borderRadius: '50%',
            border: `1px solid ${hairline}`,
            color: onSurface,
            fontFamily: 'var(--font-display)',
            fontSize: 20, fontWeight: 700, lineHeight: 1, padding: 0,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2,
          }}
        >×</button>

        <window.SearchDetailPane item={item} darkMode={darkMode} />
      </div>
    </div>
  );

  if (typeof window !== 'undefined'
      && window.ReactDOM
      && typeof window.ReactDOM.createPortal === 'function'
      && typeof document !== 'undefined') {
    return window.ReactDOM.createPortal(overlay, document.body);
  }
  return overlay;
}

// Backdrop fade keyframes — injected once.
if (typeof document !== 'undefined' && !document.getElementById('search-detail-popover-keyframes')) {
  const style = document.createElement('style');
  style.id = 'search-detail-popover-keyframes';
  style.textContent = '@keyframes search-detail-backdrop-in { from { opacity: 0; } to { opacity: 1; } }';
  document.head.appendChild(style);
}

window.SearchDetailPopover = SearchDetailPopover;
